import axios from 'axios'
/* Assets & Helpers */
import { addToRequestsList } from '@/store/helpers/accessControl'
import { APP_VERSION, APP_ANDROID_VERSION, APP_IOS_VERSION, MDW_ENDPOINT, MDW_PORTAL_ENDPOINT } from '@/assets/constants/core'
import { buildUrlParams } from '@/network/helpers/urlBuilders'
import { useSessionStore } from '@/store/session'
import { useProjectBaseStore } from '@/store/projectBase'

/** configure the default query params to be sent
@config { Object } axios config file
@return { void }
*/
const setDefaultQueryParams = config => {
    const sessionStore = useSessionStore()
    const projectBaseStore = useProjectBaseStore()
    config.params = config.params || {}
    config.params.noCacheHelper = new Date().getTime()
    config.params.token = sessionStore.getToken
	config.params.appVersion = APP_VERSION

	if (projectBaseStore.getMobileAppState && window.cordova) {
		config.params.clientApp = projectBaseStore.getDeviceInUse === 'android' ? 'Android' : 'iOS'
		config.params.appVersion = projectBaseStore.getDeviceInUse === 'android' ? APP_ANDROID_VERSION : APP_IOS_VERSION
	} else if (projectBaseStore.getMobileAppState && projectBaseStore.getDeviceInUse === 'android') {
		config.params.clientApp = 'Android'
		config.params.appVersion = APP_ANDROID_VERSION
    }
}

/** create the base instance for the axios */
const instance = axios.create({
    baseURL: MDW_ENDPOINT
})

const portalInstance = axios.create({
	baseURL: MDW_PORTAL_ENDPOINT,
	paramsSerializer (params) {
		return buildUrlParams(params).toString()
	}
})

instance.CancelToken = axios.CancelToken
instance.isCancel = axios.isCancel

/**
* create urlBuilder for use without axios, example: pdfs, images
**/
const urlIB = (config) => {
	setDefaultQueryParams(config)
	return MDW_ENDPOINT + axios.getUri(config)
}
/*************************************************
*                                                *
*                    Interceptors                *
*                                                *
*************************************************/
/** Config for request */
instance.interceptors.request.use(config => {
	if (config.downloadMobileRequest) return config

    setDefaultQueryParams(config)
	config.paramsSerializer = (params) => {
		return buildUrlParams(params, config.allowEmptyQueryParams).toString()
	}

	/* Added logic when data dont exist in post methods, to prevent browser from denying our request */
	if (config.method === 'post' && !config.data) config.data = {}
	// Adding headers for mdw, request in issue #56259
	if (import.meta.env.VITE_MOCKS !== 'true') {
		config.headers.common['X-INM-Device-System'] = navigator.platform // example Win32
		config.headers.common['X-INM-Device-System-Version'] = navigator.userAgent
	}
	/* example:
	* "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/81.0.4044.138 Safari/537.36" -> chrome
	* "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:75.0) Gecko/20100101 Firefox/75.0" -> firefox
	*/
    return config
})

/** Config for response */
instance.interceptors.response.use(config => {
	/* Success config would be here */
	if (config.data && config.data.remainingSecondsForSessionExpire) {
		useSessionStore().updateRemainingSecondsForSessionExpire(config.data.remainingSecondsForSessionExpire)
	}

	if (config.data && config.data.challenge) {
		useProjectBaseStore().showTokenChallengeModal({ response: config.data })
	}
    return config
}, error => {
	/* We want to trigger expired session modal */
	if (error.response && error.response.status === 401) {
        if (error.response && error.response.data?.code === 'token_not_found') {
            useSessionStore().forceLogout()
            window.sessionStorage.setItem('showLoginAlert', error.response.data.code)
        } else if (error.response && error.response.data?.code === 'expired_token') {
            useSessionStore().updateSessionExpireState(true)
        }
	}
    return Promise.reject(error)
})

/** Config for PB request */
portalInstance.interceptors.request.use(config => {
    setDefaultQueryParams(config)
	config.paramsSerializer = (params) => {
		return buildUrlParams(params, config.allowEmptyQueryParams).toString()
	}

	/* Added logic when data dont exist in post methods, to prevent browser from denying our request */
	if (config.method === 'post' && !config.data) config.data = {}
	// Adding headers for mdw, request in issue #56259
	config.headers.common['X-INM-Device-System'] = navigator.platform // example Win32
	config.headers.common['X-INM-Device-System-Version'] = navigator.userAgent
	/* example:
	* "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/81.0.4044.138 Safari/537.36" -> chrome
	* "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:75.0) Gecko/20100101 Firefox/75.0" -> firefox
	*/
    return config
})
/*************************************************
*                                                *
*            Axios Cancel Request Layer          *
*                                                *
*************************************************/
const axiosWithCancelableLayer = ({ method, url, data, params, isCancellable, downloadMobileRequest }) => {
	let source
	if (isCancellable) {
		source = instance.CancelToken.source()
	}

	const request = instance({
        method: method,
        url: url,
		data: data,
		params: params,
		cancelToken: isCancellable ? source.token : null,
		downloadMobileRequest: downloadMobileRequest
	})

	if (isCancellable) addToRequestsList({ request, source })
	return request
}

const axiosWithCancelablePortalLayer = ({ method, url, data, params, isCancellable }) => {
	let source
	if (isCancellable) {
		source = instance.CancelToken.source()
	}

	const request = portalInstance({
        method: method,
        url: url,
		data: data,
		params: params,
		cancelToken: isCancellable ? source.token : null
	})

	if (isCancellable) addToRequestsList({ request, source })
	return request
}
export default {
	ib: axiosWithCancelableLayer,
	portal: axiosWithCancelablePortalLayer,
	urlIB: urlIB
}
